import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { ImageCoverView, ImageScaleView } from '@play-co/timestep-core/ui';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import type { PetSpeciesID } from 'src/replicant/data/PetSpecies.schema';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { assertNever } from 'src/replicant/utils';
import * as Pet from 'src/replicant/pet';
import { getCreativeSuffix } from 'src/lib/utils';
import StateObserver from 'src/StateObserver';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';

type CloseHandler = () => void;
const ui = uiConfig.buruburu.tutorial;

export default class PopupEncyclopediaTutorial extends PopupBasicLazy {
  private tutorialImage: ImageCoverView;
  private subTitleOne: LangBitmapFontTextView;
  private pet: ImageScaleView;
  private voice: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      ...(ui.popup as ViewOpts),
      tracking: { feature: 'tutorial', $subFeature: 'popup-tutorial-step' },
    });

    this.buttonClose.removeFromSuperview();

    this.box.addSubview(new ImageScaleView(ui.frame));
    this.box.addSubview(
      new ImageScaleView({
        ...ui.frame,
        x: ui.frame.width - 1,
        flipX: true,
      }),
    );

    this.message.updateOpts({
      ...ui.message,
      width: this.box.style.width - 2 * ui.message.x,
    });

    new LangBitmapFontTextView({
      ...ui.pageIndicator,
      superview: this.box,
      localeText: () => '1/1',
    });

    this.tutorialImage = new ImageCoverView({
      width: 507,
      height: 901,
      x: 69, // nice
      y: 97,
      zIndex: -1,
      superview: this.box,
      image: 'assets/tutorial/Tut29.png',
    });

    new LangBitmapFontTextView({
      size: 24,
      font: 'Body',
      color: '#3F4E58',
      width: 500,
      height: 84,
      verticalAlign: 'center',
      align: 'center',
      x: 0,
      y: 43,
      superview: this.tutorialImage,
      localeText: () => i18n('encyclopedia.tutorial.title'),
    });

    this.subTitleOne = new LangBitmapFontTextView({
      superview: this.tutorialImage,
      size: 24,
      font: 'Body',
      color: '#9C490D',
      width: 500,
      height: 84,
      verticalAlign: 'center',
      align: 'center',
      x: 0,
      y: 136,
      wordWrap: true,
      localeText: () => i18n('encyclopedia.tutorial.subtitleOne'),
    });

    new LangBitmapFontTextView({
      superview: this.tutorialImage,
      size: 24,
      font: 'Body',
      color: '#9C490D',
      width: 500,
      height: 84,
      verticalAlign: 'center',
      align: 'center',
      x: 0,
      y: 516,
      wordWrap: true,
      localeText: () => i18n('encyclopedia.tutorial.subtitleTwo'),
    });

    this.pet = new ImageScaleView({
      tag: 'pet',
      superview: this.tutorialImage,
      width: 150,
      height: 150,
      x: 183,
      y: 289,
      scaleMethod: 'contain',
    });

    // You found me
    this.voice = new LangBitmapFontTextView({
      size: 19,
      font: 'Body',
      color: '#000000',
      width: 220,
      height: 25,
      verticalAlign: 'center',
      align: 'center',
      x: 267,
      y: 291,
      r: 0.5,
      centerAnchor: true,
      superview: this.tutorialImage,
      localeText: () => i18n('encyclopedia.tutorial.youFoundMe'),
    });

    // Close button
    new ButtonScaleViewWithText({
      ...ui.closeButton,
      superview: this.box,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close();
      },
      tracking: {
        feature: 'tutorial',
        $subFeature: 'popup-tutorial-step-close',
      },
    });
  }

  protected getAssetsGroup(): AssetGroup {
    return new AssetGroup(['assets/tutorial/Tut29.png']);
  }

  public async init(opts: {
    step: 'encyclopedia' | 'encyclopediaReminder';
    species?: PetSpeciesID;
  }) {
    const { user } = StateObserver.getState();
    const { step } = opts;
    const species = opts.species ?? 'dog';
    const petName =
      Pet.ruleset.evolutions[getPetSpeciesBucket(user)][species]?.[
        `name_${getCreativeSuffix()}`
      ];

    // Hide voice if species is defined
    this.voice.style.visible = opts.species ? false : true;

    // Set pet image
    this.pet.setImage(`assets/ui/species/linepet_${species}.png`);

    // Update subtitle
    switch (step) {
      case 'encyclopedia':
        this.subTitleOne.localeText = () =>
          i18n('encyclopedia.tutorial.subtitleOne');
        break;

      case 'encyclopediaReminder':
        this.subTitleOne.localeText = () =>
          i18n('encyclopedia.tutorialRemind.subtitleOne', { species: petName });
        break;
      default:
        assertNever(step);
    }

    super.init({
      message: i18n(`tutorial.steps.${step}`),
    });
  }
}
